import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Play, Pause, FileText, Users, Send, Eye, MousePointerClick, XCircle } from "lucide-react";
import Sidebar from "@/components/dashboard/Sidebar";
import { useBranding } from "@/lib/branding";
import {
  mailListTemplates, mailCreateTemplate, mailDeleteTemplate,
  mailListLists, mailCreateList, mailImportContacts,
  mailListCampaigns, mailCreateCampaign, mailStartCampaign, mailPauseCampaign, mailPreview,
} from "@/lib/api";

const PLACEHOLDERS = [
  { k: "{{first_name}}", d: "First name" },
  { k: "{{name}}", d: "Full name" },
  { k: "{{email}}", d: "Recipient email" },
  { k: "{{domain}}", d: "Their domain / your listing" },
  { k: "{{custom}}", d: "Custom column from CSV" },
  { k: "{{lander_url}}", d: "Public lander URL" },
  { k: "{{unsubscribe_url}}", d: "Unsubscribe (auto-added)" },
];

function TabButton({ id, active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`mail-tab-${id}`}
      className={`text-[11px] font-mono uppercase px-4 py-3 border-b-2 -mb-px flex items-center gap-2 ${
        active === id ? "border-[#03142f] text-[#03142f]" : "border-transparent text-[#525252] hover:text-[#111]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {children}
    </button>
  );
}

function TemplatesTab() {
  const [params] = useSearchParams();
  const prefillCompany = params.get("company") || "";
  const prefillDomains = (params.get("domains") || "").split(",").filter(Boolean);
  const isFollowUp = params.get("follow_up") === "1";
  const daysSilent = params.get("days_silent") || "";

  const [items, setItems] = useState([]);
  const [name, setName] = useState(
    prefillCompany
      ? isFollowUp
        ? `Follow-up · ${prefillCompany}`
        : `Outreach · ${prefillCompany}`
      : "",
  );
  const [subject, setSubject] = useState(
    prefillCompany
      ? isFollowUp
        ? `Bumping this — ${prefillDomains[0] || "quick question"}`
        : `Domain match for ${prefillCompany}`
      : "Perfect domain for {{first_name}}: {{domain}}",
  );
  const [body, setBody] = useState(() => {
    if (!prefillCompany || prefillDomains.length === 0) {
      return "Hi {{first_name}},\n\nWe hand-picked **{{domain}}** because it looks like a strong fit for your project.\n\nSee details: [{{domain}}]({{lander_url}})\n\n— Team CasperDomains";
    }
    if (isFollowUp) {
      // Shorter, more direct — the broker's already introduced themselves;
      // this is the "nudge, no fluff" ping.
      return [
        `Hi {{first_name}},`,
        ``,
        `Circling back on ${prefillDomains.map((d) => `**${d}**`).join(" / ")}${daysSilent ? ` — sent that ${daysSilent} days ago and figured it might have slipped by.` : "."}`,
        ``,
        `If it's a no, totally fine — just let me know and I'll take it off my list. If it's still interesting, quick reply and we'll get you a number today.`,
        ``,
        `— Team CasperDomains`,
      ].join("\n");
    }
    return [
      `Hi {{first_name}},`,
      ``,
      `We noticed folks from ${prefillCompany} spent time on ${prefillDomains.length > 1 ? "a few of our domains" : "one of our domains"} recently — ${prefillDomains.map((d) => `**${d}**`).join(", ")}.`,
      ``,
      `If any of these are on your radar for an upcoming project, we'd love to make an intro price work. Reply here and we'll get you sorted the same day.`,
      ``,
      `— Team CasperDomains`,
    ].join("\n");
  });
  const [preview, setPreview] = useState(null);
  const load = async () => setItems(await mailListTemplates());
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name || !subject) return toast.error("Name and subject required");
    await mailCreateTemplate({ name, subject, body_md: body });
    toast.success("Template saved");
    setName("");
    load();
  };
  const preview_ = async () => {
    const p = await mailPreview({ name: name || "preview", subject, body_md: body });
    setPreview(p);
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-[#eaecf0] p-5 bg-white space-y-3">
        <div className="overline">New template</div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name"
          data-testid="tpl-name" className="w-full text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm" />
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
          data-testid="tpl-subject" className="w-full text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12}
          data-testid="tpl-body" placeholder="Markdown body"
          className="w-full text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm outline-none focus:border-[#03142f]" />
        <div className="flex flex-wrap gap-1">
          {PLACEHOLDERS.map((p) => (
            <button key={p.k} onClick={() => setBody((b) => b + " " + p.k)} title={p.d}
              className="text-[10px] font-mono px-2 py-1 border border-[#eaecf0] hover:bg-[#F4F4F5]">
              {p.k}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={preview_} data-testid="tpl-preview"
            className="text-[11px] font-mono uppercase px-4 py-2 border border-[#eaecf0] hover:bg-[#F4F4F5]">Preview</button>
          <button onClick={create} data-testid="tpl-save"
            className="text-[11px] font-mono uppercase px-4 py-2 bg-[#03142f] text-white hover:bg-[#001D6E]">Save template</button>
        </div>
        {preview && (
          <div className="border-t border-[#eaecf0] pt-3">
            <div className="overline mb-1">Preview subject</div>
            <div className="text-sm font-mono mb-3">{preview.subject}</div>
            <div className="overline mb-1">Preview HTML</div>
            <div className="text-sm border border-[#eaecf0] p-3 bg-[#FAFAFA]" data-testid="tpl-preview-html"
              dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        )}
      </div>
      <div>
        <div className="overline mb-2">Templates ({items.length})</div>
        <div className="border border-[#eaecf0] bg-white divide-y divide-[#F4F4F5]" data-testid="tpl-list">
          {items.length === 0 && <div className="p-4 text-sm text-[#666]">No templates yet.</div>}
          {items.map((t) => (
            <div key={t.id} className="px-4 py-3 flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-sm">{t.name}</div>
                <div className="text-xs text-[#666]">{t.subject}</div>
              </div>
              <button onClick={async () => { await mailDeleteTemplate(t.id); load(); }}
                data-testid={`tpl-del-${t.id}`} className="text-[10px] font-mono uppercase text-[#BE123C] hover:underline">
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ListsTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [csv, setCsv] = useState("");
  const load = async () => {
    const r = await mailListLists();
    setItems(r);
    if (r.length && !selectedId) setSelectedId(r[0].id);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async () => {
    if (!name) return toast.error("Name required");
    const r = await mailCreateList({ name });
    setSelectedId(r.id);
    setName("");
    load();
  };
  const importIt = async () => {
    if (!selectedId || !csv) return toast.error("Pick a list + paste some emails");
    const r = await mailImportContacts(selectedId, csv);
    toast.success(`Added ${r.added} · skipped ${r.skipped} · total ${r.total}`);
    setCsv("");
    load();
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-[#eaecf0] p-5 bg-white space-y-3">
        <div className="overline">Lists</div>
        <div className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New list name"
            data-testid="list-name" className="flex-1 text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm" />
          <button onClick={create} data-testid="list-create"
            className="text-[11px] font-mono uppercase px-4 py-2 bg-[#03142f] text-white hover:bg-[#001D6E]">Create</button>
        </div>
        <div className="divide-y divide-[#F4F4F5] border border-[#eaecf0]">
          {items.map((l) => (
            <button key={l.id} onClick={() => setSelectedId(l.id)}
              data-testid={`list-select-${l.id}`}
              className={`w-full text-left px-4 py-2 text-sm font-mono flex justify-between ${
                selectedId === l.id ? "bg-[#EEF2FF]" : "hover:bg-[#FAFAFA]"
              }`}>
              <span>{l.name}</span>
              <span className="text-[#666]">{l.contact_count} contacts</span>
            </button>
          ))}
        </div>
      </div>
      <div className="border border-[#eaecf0] p-5 bg-white space-y-3">
        <div className="overline">Import contacts (CSV / paste)</div>
        <div className="text-[11px] font-mono text-[#666]">
          Format per line: <code>email, name, domain, custom</code>. Lines starting with <code>#</code> are ignored.
        </div>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={10}
          data-testid="list-import-textarea" placeholder={"alex@example.com, Alex Roe, example.com, sr-designer\nsam@aisurge.io"}
          className="w-full text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm outline-none focus:border-[#03142f]" />
        <button onClick={importIt} disabled={!selectedId} data-testid="list-import-submit"
          className="text-[11px] font-mono uppercase px-4 py-2 bg-[#03142f] text-white hover:bg-[#001D6E] disabled:opacity-40">
          Import to selected list
        </button>
      </div>
    </div>
  );
}

function CampaignsTab() {
  const [tpls, setTpls] = useState([]);
  const [lists, setLists] = useState([]);
  const [camps, setCamps] = useState([]);
  const [name, setName] = useState("");
  const [tplId, setTplId] = useState("");
  const [listId, setListId] = useState("");
  const [rate, setRate] = useState(30);

  const load = async () => {
    const [t, l, c] = await Promise.all([mailListTemplates(), mailListLists(), mailListCampaigns()]);
    setTpls(t); setLists(l); setCamps(c);
    if (t.length && !tplId) setTplId(t[0].id);
    if (l.length && !listId) setListId(l[0].id);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!name || !tplId || !listId) return toast.error("Name, template and list required");
    await mailCreateCampaign({ name, template_id: tplId, list_id: listId, rate_per_hour: Number(rate) || 30 });
    toast.success("Campaign created");
    setName("");
    load();
  };
  const startIt = async (cid) => { await mailStartCampaign(cid); toast.success("Campaign started"); load(); };
  const pauseIt = async (cid) => { await mailPauseCampaign(cid); toast.success("Campaign paused"); load(); };

  return (
    <div className="space-y-6">
      <div className="border border-[#eaecf0] p-5 bg-white space-y-3">
        <div className="overline">New campaign</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name"
            data-testid="camp-name" className="text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm" />
          <select value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="camp-tpl"
            className="text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm bg-white">
            <option value="">Template…</option>
            {tpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={listId} onChange={(e) => setListId(e.target.value)} data-testid="camp-list"
            className="text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm bg-white">
            <option value="">List…</option>
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.contact_count})</option>)}
          </select>
          <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Rate/hr"
            data-testid="camp-rate" min={1} max={500}
            className="text-sm font-mono px-2 py-1.5 border border-[#eaecf0] rounded-sm" />
        </div>
        <button onClick={create} data-testid="camp-create"
          className="text-[11px] font-mono uppercase px-4 py-2 bg-[#03142f] text-white hover:bg-[#001D6E]">
          Create campaign
        </button>
      </div>
      <div>
        <div className="overline mb-2">Campaigns</div>
        <div className="border border-[#eaecf0] bg-white divide-y divide-[#F4F4F5]" data-testid="mail-campaigns-list">
          {camps.length === 0 && <div className="p-4 text-sm text-[#666]">No campaigns yet.</div>}
          {camps.map((c) => (
            <div key={c.id} data-testid={`campaign-${c.id}`} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-mono text-sm">{c.name}</div>
                <div className="text-[11px] font-mono text-[#666] uppercase">status · {c.status} · {c.rate_per_hour}/hr</div>
                <div data-testid={`campaign-stats-${c.id}`} className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] font-mono text-[#525252]">
                  <span>Total <b className="text-[#111]">{c.stats?.total || 0}</b></span>
                  <span>Sent <b className="text-[#111]">{c.stats?.sent || 0}</b></span>
                  <span>Delivered <b className="text-[#111]">{c.stats?.delivered || 0}</b></span>
                  <span>Queued <b className="text-[#111]">{c.stats?.queued || 0}</b></span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />Opens <b className="text-[#047857]">{c.stats?.total_opens || 0}</b> ({c.stats?.opened || 0} unique)</span>
                  <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" />Clicks <b className="text-[#047857]">{c.stats?.total_clicks || 0}</b> ({c.stats?.clicked || 0} unique)</span>
                  <span className="flex items-center gap-1"><XCircle className="w-3 h-3" />Bounced <b className="text-[#BE123C]">{c.stats?.bounced || 0}</b></span>
                  <span>Unsub <b className="text-[#B45309]">{c.stats?.unsubscribed || 0}</b></span>
                  <span>Complaints <b className="text-[#BE123C]">{c.stats?.complained || 0}</b></span>
                  {c.stats?.forward_suspected > 0 && (
                    <span title="Same email opened from >1 distinct IP — likely forwarded">
                      Forwarded? <b className="text-[#B45309]">{c.stats.forward_suspected}</b>
                    </span>
                  )}
                  {(c.stats?.unique_countries || []).length > 0 && (
                    <span title={c.stats.unique_countries.join(", ")}>
                      Countries <b className="text-[#111]">{c.stats.unique_countries.length}</b>
                    </span>
                  )}
                  {c.stats?.unique_ips_count > 0 && (
                    <span>IPs <b className="text-[#111]">{c.stats.unique_ips_count}</b></span>
                  )}
                </div>
                {(c.stats?.top_links || []).length > 0 && (
                  <div className="mt-2 text-[10px] font-mono text-[#666]">
                    <span className="uppercase mr-2">Top links:</span>
                    {c.stats.top_links.slice(0, 3).map((l, i) => (
                      <span key={i} className="mr-3">
                        <b className="text-[#111]">{l.count}×</b>{" "}
                        <span className="text-[#525252]">{l.url.length > 50 ? l.url.slice(0, 50) + "…" : l.url}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {c.status !== "sending" && (
                  <button onClick={() => startIt(c.id)} data-testid={`camp-start-${c.id}`}
                    className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#047857] text-[#047857] hover:bg-[#047857] hover:text-white">
                    <Play className="w-3 h-3 inline mr-1" /> Start
                  </button>
                )}
                {c.status === "sending" && (
                  <button onClick={() => pauseIt(c.id)} data-testid={`camp-pause-${c.id}`}
                    className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#B45309] text-[#B45309] hover:bg-[#B45309] hover:text-white">
                    <Pause className="w-3 h-3 inline mr-1" /> Pause
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MailPage() {
  const brand = useBranding();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("compose") ? "templates" : "templates");
  const composeCompany = params.get("company") || "";
  const isFollowUp = params.get("follow_up") === "1";
  const daysSilent = params.get("days_silent") || "";
  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="mail-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center gap-3">
          <span className="overline">{brand.brand_name}</span>
          <span className="text-[#D4D4D8]">/</span>
          <span className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4" strokeWidth={1.5} /> Mail</span>
        </header>
        <div className="border-b border-[#eaecf0] px-6 flex gap-1">
          <TabButton id="templates" active={tab} onClick={setTab} icon={FileText}>Templates</TabButton>
          <TabButton id="lists" active={tab} onClick={setTab} icon={Users}>Lists</TabButton>
          <TabButton id="campaigns" active={tab} onClick={setTab} icon={Send}>Campaigns</TabButton>
        </div>
        <div className="flex-1 overflow-auto p-6">
          {composeCompany && tab === "templates" && (
            <div
              className="mb-4 px-4 py-3 border rounded-md text-sm flex items-start gap-3"
              style={
                isFollowUp
                  ? { background: "#fef3c7", borderColor: "#fbbf24", color: "#92400e" }
                  : { background: "#e0f2fe", borderColor: "#7dd3fc", color: "#075985" }
              }
              data-testid="mail-prefill-banner"
            >
              <Mail className="w-4 h-4 mt-0.5" strokeWidth={1.8} />
              <div>
                <strong>
                  {isFollowUp
                    ? `Follow-up nudge for ${composeCompany}${daysSilent ? ` · ${daysSilent} days silent` : ""}`
                    : `Draft prefilled for ${composeCompany}`}
                </strong>{" "}
                — {isFollowUp
                  ? "Shorter, more direct copy pre-filled. Trim further, save the template, and hit them with a fresh angle."
                  : "edit the subject, body, and template name below, then hit Save template to keep it or Preview to test it."}
              </div>
            </div>
          )}
          {tab === "templates" && <TemplatesTab />}
          {tab === "lists" && <ListsTab />}
          {tab === "campaigns" && <CampaignsTab />}
        </div>
      </main>
    </div>
  );
}
